import { isMobile } from 'react-device-detect';

/**
 * Platform detection for touch-first behavior that must not change when the
 * viewport is resized (e.g. tune-list filter chips disabled on phones/tablets).
 * For layout reflow, use useMediaQuery / CSS breakpoints instead.
 */
export function isMobilePlatform() {
  return isMobile;
}

/**
 * True when running inside a Capacitor native shell (Android/iOS app).
 */
export function isCapacitorNative() {
  if (typeof window === 'undefined') return false;
  const cap = window.Capacitor;
  if (cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) {
    return true;
  }
  // Capacitor Android WebView (androidScheme https → https://localhost).
  if (window.location
    && window.location.protocol === 'https:'
    && window.location.hostname === 'localhost') {
    return true;
  }
  return false;
}

/**
 * True when running as the Tunebook Android app (Capacitor on Android).
 */
export function isAndroidApp() {
  if (!isCapacitorNative()) return false;
  const cap = window.Capacitor;
  return cap.getPlatform && cap.getPlatform() === 'android';
}

/**
 * Prefer native ExoPlayer foreground-service playback on Android app builds.
 */
export function prefersNativeMediaPlayback() {
  return isAndroidApp();
}

/** localStorage kill switch: set to 'off' to keep mobile web on the Web Audio synth. */
export const HOSTED_ABC_PLAYBACK_STORAGE_KEY = 'tunebook_hosted_abc_playback';

/** Phone/tablet browser, not the Capacitor app shell. */
export function isMobileWebBrowser() {
  return isMobilePlatform() && !isCapacitorNative();
}

/**
 * Mobile browsers suspend Web Audio when backgrounded or the screen is off, so
 * notation is pre-rendered to WAV and played through an <audio> element there.
 */
export function isMobileWebHostedAudioEnabled() {
  if (!isMobileWebBrowser()) return false;
  try {
    if (typeof localStorage !== 'undefined'
      && localStorage.getItem(HOSTED_ABC_PLAYBACK_STORAGE_KEY) === 'off') {
      return false;
    }
  } catch (e) { /* ignore */ }
  return true;
}

/**
 * ABC notation is rendered to WAV and played by a host media player (ExoPlayer
 * in the Android app, a persistent <audio> element on mobile web) rather than
 * the live Web Audio synth.
 */
export function prefersHostedAbcPlayback() {
  return isAndroidApp() || isMobileWebHostedAudioEnabled();
}

/**
 * Google Cast Web Sender SDK (cast_sender.js) only works in desktop Chromium.
 * Capacitor WebView on Android/iOS cannot load the Cast framework.
 */
export function isCastWebSdkSupported() {
  if (isCapacitorNative()) return false;
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile/.test(ua)) return false;
  return true;
}

/**
 * Desktop Chromium-based Google Chrome (not Edge, Opera, or mobile Chrome).
 * TuneBook Helper is a desktop Chrome extension.
 */
export function isChromiumDesktopBrowser() {
  if (isMobilePlatform()) return false;
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Chrome\//.test(ua)
    && !/Edg\//.test(ua)
    && !/OPR\//.test(ua)
    && !/SamsungBrowser\//.test(ua);
}
